/**
 * LinkButton - a real <button> dressed as an inline hyperlink.
 *
 * Atomic Design role: Atom. For the in-form escape hatches that open
 * something rather than submit anything — "+ Add a New Building" under the
 * Building picker, "Quick Add User" under the hours editor, "Download CSV
 * Template". They read as links (brand-colored text, no box) so they don't
 * compete with the form's real actions, but they ARE buttons: they open a
 * modal or run a handler, never navigate, so they must not be <a> tags.
 *
 * Always type="button" by default — every one of these lives inside a <form>,
 * and the browser default (submit) would post the form on click.
 *
 * Keeps the 44px touch floor via min-h-44px without padding, so the text still
 * lines up with the field label above it.
 *
 * @example
 * <LinkButton onClick={() => setQuickAddOpen(true)}>Quick Add User</LinkButton>
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export type LinkButtonSize = 'sm' | 'md';

export interface LinkButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** 'sm' = 13px (dense helper rows), 'md' = 14px (default, under a field). */
	size?: LinkButtonSize;
	/** Underline at rest, not just on hover — for a lone link in body copy. */
	underline?: boolean;
	/** Overrides the atom's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * STYLE MAPS
 * ============================================ */

const SIZE_CLASSES: Record<LinkButtonSize, string> = {
	sm: 'text-13px',
	md: 'text-14px',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const LinkButton: React.FC<LinkButtonProps> = ({ size = 'md', underline = false, dataRef = 'atom-link-button', type = 'button', className = '', children, ...rest }) => (
	<button type={type} className={`link-button inline-flex items-center gap-4px min-h-44px text-left ${SIZE_CLASSES[size]} font-sans font-medium text-brand-primary ${underline ? 'underline' : 'hover:underline'} hover:text-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`.trim()} data-ref={dataRef} {...rest}>
		{children}
	</button>
);

export default LinkButton;
